import {
  loadPdf,
  loadPdfWithPassword,
  type PdfDocument,
  PasswordRequiredError,
  WrongPasswordError,
  InvalidPdfError,
} from '../converter/pdf-loader'

const ACCEPTED_MIME = 'application/pdf'

export type UploadedPdf = {
  pdf: PdfDocument
  filename: string
  fileSize: number
  numPages: number
}

/**
 * Creates the upload area inside the given container.
 * Handles drag & drop and file picker, validates that the
 * selected file is a PDF, then loads it with PDF.js and
 * calls onPdfLoaded with the result.
 *
 * If the PDF is password-protected, shows a password prompt
 * and retries with the entered password (PRD §35).
 */
export function createUploader(
  container: HTMLElement,
  onPdfLoaded: (uploaded: UploadedPdf) => void,
): void {
  let lastFile: File | null = null

  renderUploadArea(container)

  function renderUploadArea(container: HTMLElement): void {
    container.innerHTML = `
    <h1 class="app-title">PDF to Image</h1>
    <p class="app-subtitle">Convert PDF pages to JPG or PNG — right in your browser</p>
    <div class="uploader">
      <div class="drop-zone" id="dropZone" role="button" tabindex="0" aria-label="Drop a PDF here or click to browse">
        <p class="drop-zone__text">Drop your PDF here</p>
        <button type="button" class="drop-zone__button" id="browseButton">Browse PDF</button>
        <input type="file" class="drop-zone__input" id="fileInput" accept="${ACCEPTED_MIME}" />
      </div>
      <p class="privacy-note">Your files never leave your device.</p>
      <p class="error-message" id="errorMessage" role="alert" aria-live="polite"></p>
    </div>
  `

    const dropZone = container.querySelector<HTMLDivElement>('#dropZone')!
    const fileInput = container.querySelector<HTMLInputElement>('#fileInput')!
    const browseButton =
      container.querySelector<HTMLButtonElement>('#browseButton')!

    browseButton.addEventListener('click', () => fileInput.click())
    dropZone.addEventListener('click', (e) => {
      if (e.target !== browseButton) fileInput.click()
    })
    dropZone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        fileInput.click()
      }
    })

    dropZone.addEventListener('dragover', (e) => {
      e.preventDefault()
      dropZone.classList.add('dragover')
    })
    dropZone.addEventListener('dragleave', () => {
      dropZone.classList.remove('dragover')
    })
    dropZone.addEventListener('drop', (e) => {
      e.preventDefault()
      dropZone.classList.remove('dragover')
      const file = e.dataTransfer?.files?.[0]
      if (file) void handleFile(file)
    })

    fileInput.addEventListener('change', () => {
      const file = fileInput.files?.[0]
      if (file) void handleFile(file)
      fileInput.value = ''
    })
  }

  function renderPasswordPrompt(
    container: HTMLElement,
    isWrongPassword: boolean,
  ): void {
    container.innerHTML = `
    <div class="password-panel">
      <h2 class="password-title">Password Protected PDF</h2>
      <p class="password-hint">This PDF is password protected. Enter the password to unlock it.</p>
      <p class="password-filename" id="passwordFilename"></p>
      <form class="password-form" id="passwordForm">
        <label for="passwordInput" class="password-form__label">Password</label>
        <input
          type="password"
          id="passwordInput"
          class="password-form__input"
          autocomplete="current-password"
          aria-label="PDF password"
        />
        <p class="error-message" id="passwordError" role="alert" aria-live="polite"></p>
        <button type="submit" class="password-form__button">Unlock PDF</button>
        <button type="button" class="password-form__cancel" id="passwordCancel">Cancel</button>
      </form>
    </div>
  `

    const filenameEl =
      container.querySelector<HTMLParagraphElement>('#passwordFilename')!
    if (lastFile) filenameEl.textContent = lastFile.name

    const errorEl =
      container.querySelector<HTMLParagraphElement>('#passwordError')!
    if (isWrongPassword) {
      errorEl.textContent = 'Incorrect password. Please try again.'
    }

    const form = container.querySelector<HTMLFormElement>('#passwordForm')!
    const passwordInput =
      container.querySelector<HTMLInputElement>('#passwordInput')!
    const cancelButton =
      container.querySelector<HTMLButtonElement>('#passwordCancel')!

    passwordInput.focus()

    form.addEventListener('submit', (e) => {
      e.preventDefault()
      const password = passwordInput.value
      if (!password || !lastFile) return
      void tryUnlockWithPassword(lastFile, password)
    })

    cancelButton.addEventListener('click', () => {
      lastFile = null
      renderUploadArea(container)
    })
  }

  async function tryUnlockWithPassword(
    file: File,
    password: string,
  ): Promise<void> {
    try {
      const pdf = await loadPdfWithPassword(file, password)
      onPdfLoaded({
        pdf,
        filename: file.name,
        fileSize: file.size,
        numPages: pdf.numPages,
      })
    } catch (err) {
      if (err instanceof WrongPasswordError) {
        renderPasswordPrompt(container, true)
      } else if (err instanceof InvalidPdfError) {
        renderUploadArea(container)
        const errorMessage =
          container.querySelector<HTMLParagraphElement>('#errorMessage')!
        errorMessage.textContent = err.message
      } else {
        console.error(err)
        renderUploadArea(container)
        const errorMessage =
          container.querySelector<HTMLParagraphElement>('#errorMessage')!
        errorMessage.textContent =
          'We couldn\u2019t read this PDF. The file may be damaged or unsupported.'
      }
    }
  }

  async function handleFile(file: File): Promise<void> {
    lastFile = file
    const errorMessage =
      container.querySelector<HTMLParagraphElement>('#errorMessage')!
    if (errorMessage) errorMessage.textContent = ''

    if (file.type !== ACCEPTED_MIME) {
      if (errorMessage) {
        errorMessage.textContent =
          "This file isn't a PDF. Please select a PDF file."
      }
      return
    }

    try {
      const pdf = await loadPdf(file)
      onPdfLoaded({
        pdf,
        filename: file.name,
        fileSize: file.size,
        numPages: pdf.numPages,
      })
    } catch (err) {
      if (err instanceof PasswordRequiredError) {
        renderPasswordPrompt(container, false)
      } else if (err instanceof InvalidPdfError) {
        if (errorMessage) errorMessage.textContent = err.message
      } else {
        console.error(err)
        if (errorMessage) {
          errorMessage.textContent =
            'We couldn\u2019t read this PDF. The file may be damaged or unsupported.'
        }
      }
    }
  }
}
